import type { LucideIcon } from 'lucide-react'
import {
  Building2,
  Briefcase,
  Database,
  Folder,
  Globe,
  Inbox,
  LayoutDashboard,
  LifeBuoy,
  Network,
  Shield,
  UserCheck,
  Users,
  UserCog,
} from 'lucide-react'

export type NavItemConfig = {
  label: string
  icon: LucideIcon
  /** Route path. Use `dashboard` to resolve the current user's dashboard_path at runtime. */
  to: string | 'dashboard'
  permission?: string
}

export type DashboardWidgetConfig = {
  label: string
  /** Static fallback while loading; live value comes from `statKey` when set. */
  value: string
  permission?: string
  highlight?: boolean
  to?: string
  /** Maps to a key on GET /api/v1/dashboard/stats — omit for non-stats widgets. */
  statKey?:
    | 'under_review'
    | 'review_closed'
    | 'review_inbox'
    | 'drafts'
    | 'archived'
    | 'pending_doctors_count'
    | 'approved_doctors_count'
    | 'open_support_count'
    | 'total_cases'
    | 'global_cases_count'
    | 'private_cases_count'
    | 'recent_signups_count'
}

export type DashboardActionConfig = {
  label: string
  permission: string
  to?: string
}

/** Sidebar navigation — items without a matching permission are hidden. */
export const MAIN_NAV_ITEMS: NavItemConfig[] = [
  { label: 'Dashboard', icon: LayoutDashboard, to: 'dashboard' },
  { label: 'Roles', icon: Shield, to: '/roles', permission: 'roles-list' },
  { label: 'My Cases', icon: Folder, to: '/cases', permission: 'cases-list' },
  { label: 'Cases for Review', icon: Inbox, to: '/review-inbox', permission: 'cases-review-inbox' },
  { label: 'National hub', icon: Globe, to: '/national-hub', permission: 'national-hub-list' },
  { label: 'Assistants', icon: Users, to: '/doctor/assistants', permission: 'assistants-list' },
  { label: 'Support', icon: LifeBuoy, to: '/support', permission: 'support-requests-list' },
]

export const ADMIN_NAV_ITEMS: NavItemConfig[] = [
  {
    label: 'Reviewers',
    icon: UserCog,
    to: '/admin/reviewers',
    permission: 'reviewers-list',
  },
  {
    label: 'Hospitals',
    icon: Building2,
    to: '/admin/hospitals',
    permission: 'hospitals-list',
  },
  {
    label: 'Departments',
    icon: Network,
    to: '/admin/departments',
    permission: 'departments-list',
  },
  {
    label: 'Positions',
    icon: Briefcase,
    to: '/admin/positions',
    permission: 'positions-list',
  },
  {
    label: 'Procedures',
    icon: Folder,
    to: '/admin/procedures',
    permission: 'procedures-list',
  },
  {
    label: 'Comorbidities',
    icon: Database,
    to: '/admin/comorbidities',
    permission: 'comorbidities-list',
  },
  {
    label: 'Complications',
    icon: LifeBuoy,
    to: '/admin/complications',
    permission: 'complications-list',
  },
  {
    label: 'Doctors',
    icon: UserCheck,
    to: '/admin/doctor-approvals',
    permission: 'doctors-list',
  },
  {
    label: 'Users',
    icon: Users,
    to: '/users',
    permission: 'users-list',
  },
  {
    label: 'Password requests',
    icon: UserCheck,
    to: '/password-management',
    permission: 'reset-password-list',
  },
  {
    label: 'Support requests',
    icon: LifeBuoy,
    to: '/admin/support',
    permission: 'support-requests-list-all',
  },
]

/**
 * Dashboard KPI tiles — shown when permission passes AND (for statKey widgets)
 * the API response includes that key (role-scoped payload).
 */
export const DASHBOARD_WIDGETS: DashboardWidgetConfig[] = [
  {
    label: 'Pending doctors',
    value: '—',
    permission: 'doctors-list',
    highlight: true,
    to: '/admin/doctor-approvals',
    statKey: 'pending_doctors_count',
  },
  {
    label: 'Approved doctors',
    value: '—',
    permission: 'users-list',
    statKey: 'approved_doctors_count',
  },
  {
    label: 'Open support',
    value: '—',
    permission: 'support-requests-list-all',
    to: '/admin/support',
    highlight: true,
    statKey: 'open_support_count',
  },
  {
    label: 'Total cases',
    value: '—',
    permission: 'cases-list',
    to: '/cases',
    statKey: 'total_cases',
  },
  {
    label: 'Public cases',
    value: '—',
    permission: 'national-hub-list',
    to: '/national-hub',
    statKey: 'global_cases_count',
  },
  {
    label: 'Private cases',
    value: '—',
    permission: 'cases-list',
    to: '/cases',
    statKey: 'private_cases_count',
  },
  {
    label: 'Recent signups',
    value: '—',
    permission: 'users-list',
    statKey: 'recent_signups_count',
  },
  {
    label: 'Under review',
    value: '—',
    permission: 'cases-list',
    highlight: true,
    to: '/cases',
    statKey: 'under_review',
  },
  {
    label: 'Review closed',
    value: '—',
    permission: 'cases-list',
    to: '/cases',
    statKey: 'review_closed',
  },
  { label: 'Drafts', value: '—', permission: 'cases-list', to: '/cases', statKey: 'drafts' },
  { label: 'Archived', value: '—', permission: 'cases-list', to: '/cases', statKey: 'archived' },
  {
    label: 'Cases for Review',
    value: '—',
    permission: 'cases-review-inbox',
    highlight: true,
    to: '/review-inbox',
    statKey: 'review_inbox',
  },
]

export const DASHBOARD_ACTIONS: DashboardActionConfig[] = [
  { label: 'Upload a New Case', permission: 'cases-create', to: '/cases/new' },
  { label: 'My Cases', permission: 'cases-list', to: '/cases' },
  { label: 'Doctors', permission: 'doctors-list', to: '/admin/doctor-approvals' },
  { label: 'Manage Users', permission: 'users-list', to: '/users' },
  { label: 'Password Queue', permission: 'reset-password-list', to: '/password-management' },
  { label: 'Support Inbox', permission: 'support-requests-list-all', to: '/admin/support' },
]

/** Role landing URLs — same dashboard UI, different entry route after login. */
export const DASHBOARD_ROUTE_PATHS = [
  '/dashboard',
  '/admin/dashboard',
  '/reviewer/dashboard',
  '/assistant/dashboard',
] as const
